import { CSSProperties, useCallback, useMemo } from 'react';
import { Button, ButtonGroup } from '@cbhq/cds-web/buttons';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { HeroSquare, Pictogram } from '@cbhq/cds-web/illustrations';
import { Grid, HStack, VStack } from '@cbhq/cds-web/layout';
import {
  TextBody,
  TextDisplay3,
  TextHeadline,
  TextTitle3,
  TextTitle4,
} from '@cbhq/cds-web/typography';

import { ReactComponent as BlocklordsLogoImg } from ':cloud/assets/Products/BlocklordsLogo.svg';
import { ReactComponent as CattownLogoImg } from ':cloud/assets/Products/CattownLogo.svg';
import PaymasterBlocklordsImg from ':cloud/assets/Products/PaymasterBlocklords.png';
import { EventName, logClick, useLogViewOnMount } from ':cloud/init/clientAnalytics/logging';
import { PAYMASTER_ERC20_CONTACT_US_LINK } from ':cloud/utils/links';
import { onExternalLinkPress } from ':cloud/utils/onExternalLinkPress';
import { openUrlInNewWindow } from ':cloud/utils/openUrlInNewWindow';

import { FULL_PAGE_MAX_WIDTH } from '../product/constants';

const BLOCKLORDS_LINK = 'https://blocklords.com/';
const CAT_TOWN_LINK = 'https://cat.town/';

const imageStyle: CSSProperties = {
  marginRight: '100px',
};
type Erc20PaymasterCardProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  cta?: string;
  linkUrl?: string;
};
function Erc20PaymasterCard({ icon, title, description, cta, linkUrl }: Erc20PaymasterCardProps) {
  const hasLink = cta && linkUrl;

  return (
    <VStack borderRadius="roundedLarge" background="backgroundAlternate">
      <VStack spacing={5} spacingBottom={hasLink ? 2 : 5}>
        {icon}
        <TextHeadline as="p" spacingTop={3} spacingBottom={0.5}>
          {title}
        </TextHeadline>
        <TextBody as="p" color="foregroundMuted">
          {description}
        </TextBody>
      </VStack>
      {hasLink && (
        <VStack spacingBottom={3} spacingHorizontal={2} alignItems="flex-start">
          <Button
            variant="primary"
            endIcon="diagonalUpArrow"
            onPress={onExternalLinkPress(linkUrl)}
            accessibilityLabel={`${cta} + opens in new window`}
            transparent
            compact
          >
            {cta}
          </Button>
        </VStack>
      )}
    </VStack>
  );
}

export function Erc20PaymasterLanding() {
  const { isDesktop } = useBreakpoints();

  const responsiveCardsTemplateColumns = useMemo(() => {
    return isDesktop ? '1fr 1fr' : '1fr';
  }, [isDesktop]);

  useLogViewOnMount(EventName.paymaster_erc20_landing_view);

  const handleContactUsPress = useCallback(() => {
    openUrlInNewWindow(PAYMASTER_ERC20_CONTACT_US_LINK);
    logClick(EventName.paymaster_erc20_contact_us_click);
  }, []);

  return (
    <VStack gap={10}>
      <HStack
        maxWidth={FULL_PAGE_MAX_WIDTH}
        gap={9}
        spacingTop={4}
        spacingBottom={10}
        justifyContent="space-between"
        flexWrap={isDesktop ? 'nowrap' : 'wrap-reverse'}
      >
        <VStack justifyContent="center" maxWidth={500} minWidth={400}>
          <TextDisplay3 as="h1">Enable your users to pay for gas in ERC-20 tokens</TextDisplay3>
          <TextTitle4 as="h2" color="foregroundMuted" spacingTop={1} spacingBottom={7}>
            Further streamline your app experience by enabling users to pay for gas with ERC-20
            tokens.
          </TextTitle4>

          <ButtonGroup>
            <Button variant="primary" onPress={handleContactUsPress}>
              Contact us
            </Button>
          </ButtonGroup>
        </VStack>
        <img width={400} src={PaymasterBlocklordsImg} alt="blocklords-img" style={imageStyle} />
      </HStack>
      <VStack gap={3}>
        <TextTitle3 as="h3">Why ERC-20 Paymaster?</TextTitle3>
        <Grid gap={5} templateColumns={responsiveCardsTemplateColumns}>
          <Erc20PaymasterCard
            icon={<Pictogram name="custodialJourney" />}
            description="Enable users to pay for gas without ever needing ETH."
            title="Streamline user experience"
          />
          <Erc20PaymasterCard
            icon={<HeroSquare name="dappsL2Support" dimension="200x200" scaleMultiplier={0.24} />}
            description="Transform your token into gas money."
            title="Increase token utility"
          />
        </Grid>
      </VStack>
      <VStack gap={3}>
        <TextTitle3 as="h3">How customers are using ERC-20 Paymaster</TextTitle3>
        <Grid gap={5} templateColumns={responsiveCardsTemplateColumns}>
          <Erc20PaymasterCard
            icon={<BlocklordsLogoImg />}
            description="Blocklords is a medieval grand strategy game that lets users pay for gas with the game's token, LRDS. This gives LRDS more utility while helping to increase demand."
            title="Blocklords"
            cta="Try it out on Blocklords"
            linkUrl={BLOCKLORDS_LINK}
          />
          <Erc20PaymasterCard
            icon={<CattownLogoImg />}
            description="Cat Town is an idle game that lets users pay for gas with the game's token, $KIBBLE. This helps reduce drop-off from users leaving the game to acquire ETH."
            title="Cat Town"
            cta="Try it out on Cat Town"
            linkUrl={CAT_TOWN_LINK}
          />
        </Grid>
      </VStack>
    </VStack>
  );
}
